import React, { Component } from 'react';
import Bifrost from '@techzolo/bifrost-authenticate';
import { initializeBifrost, getDevToken, redirectToLogin, setCachedBifrostToken, clearBifrostSession } from '../utils/authHelper';
import { populateUserData } from '../config/jwt_token';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import AppBar from 'material-ui/AppBar';
import RaisedButton from 'material-ui/RaisedButton';
import logo from '../images/logo.jpg';
import Osms from '../images/vopr.png';

const server_address = process.env.REACT_APP_SERVER_ADDRESS;

// Public paths — accessible without Bifrost authentication.
// These pages were open to everyone before the Bifrost integration.
const PUBLIC_PATHS = [
  '/payment/payment-request-preview',
  '/payment/payment-request-preview/timeline',
  '/view/pngFile',
  '/view/pdfFile',
];

/**
 * BifrostAuthGate
 * 
 * Wraps the entire app. Handles token initialization and user profile fetching.
 * Does NOT handle login/logout UI — that's still done by Login.js (same as before).
 * 
 * States:
 *   - loading: Fetching user profile
 *   - ready + no error: User authenticated, render children
 *   - ready + error: Auth failed, show Access Denied (WITHOUT Header/CitySelect)
 *   - not logged in on staging/prod: Redirect to Bifrost login
 *   - not logged in on localhost: Render children (Login.js handles)
 */
class BifrostAuthGate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ready: false,
      error: null,
    };
  }

  async componentWillMount() {
    try {
      // Skip auth entirely for public pages (accessible without login)
      const currentPath = window.location.pathname;
      if (PUBLIC_PATHS.includes(currentPath)) {
        this.setState({ ready: true });
        return;
      }

      const hostname = window.location.hostname;
      const isLocalDev = (hostname === 'localhost' || hostname === '127.0.0.1');
      const devToken = getDevToken();

      // Check if user just logged out — prevent auto-re-login
      // Don't redirect to Bifrost login — Google's active session would
      // auto-authenticate and log the user right back in.
      // Instead, render the app normally (Login component at '/').
      if (sessionStorage.getItem('zaps_just_logged_out')) {
        sessionStorage.removeItem('zaps_just_logged_out');
        console.log('[BifrostAuthGate] User just logged out');
        // Initialize Bifrost so Login component can redirect to the correct
        // Bifrost login URL (staging/prod) when user clicks "Login with Google"
        initializeBifrost();
        this.setState({ ready: true });
        return;
      }

      if (isLocalDev && devToken) {
        // ---- LOCAL DEV MODE ----
        console.log('[BifrostAuthGate] Local dev mode — using hardcoded DEV_TOKEN');
        // Don't call setDevToken yet — only after auth succeeds

        let response;
        try {
          response = await fetch(`${server_address}/api/v1/user/me`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${devToken}`,
            },
          });
        } catch (err) {
          this.setState({
            ready: true,
            error: `Backend unreachable at ${server_address}. Make sure the backend is running (npm start in /zaps).`,
          });
          return;
        }

        // Read body ONCE
        let responseData = {};
        try {
          responseData = await response.json();
        } catch (e) {
          // invalid JSON
        }

        if (response.ok && responseData.data) {
          // Auth SUCCESS — populate user data and cache the app JWT
          populateUserData(responseData.data);
          if (responseData.data.token) {
            setCachedBifrostToken(responseData.data.token);
          }
          this.setState({ ready: true });
        } else {
          // Auth FAILED — show error, do NOT set token
          const errorMsg = responseData.message || `Authentication failed (HTTP ${response.status})`;
          this.setState({ ready: true, error: errorMsg });
        }
        return;
      }

      // ---- NORMAL BIFROST MODE ----
      initializeBifrost();

      // Helper: exchange Bifrost token for app JWT via /api/v1/user/me
      const completeAuth = async () => {
        let bifrostToken;
        let userEmail = '';

        try {
          bifrostToken = await Bifrost.getAccessToken();
        } catch (err) {
          this.setState({ ready: true, error: 'Could not retrieve Bifrost token.' });
          return;
        }

        // Get email from Bifrost SDK (Zotag pattern)
        // Cognito access tokens don't contain email — getUserAttributes() does
        try {
          const attrs = await Bifrost.getUserAttributes();
          userEmail = (attrs && attrs.email) ? attrs.email : '';
        } catch (err) {
          console.warn('[BifrostAuthGate] getUserAttributes failed (non-fatal):', err.message);
          // Non-fatal — backend falls back to cognito_sub lookup
        }

        // Call /api/v1/user/me with token + email
        // Backend resolves HRMS user by email, issues app JWT
        const meUrl = server_address + '/api/v1/user/me'
          + (userEmail ? '?email=' + encodeURIComponent(userEmail) : '');
        let response;
        try {
          response = await fetch(meUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + bifrostToken,
            },
          });
        } catch (err) {
          this.setState({ ready: true, error: 'Backend unreachable: ' + err.message });
          return;
        }

        var responseData = {};
        try {
          responseData = await response.json();
        } catch (e) {
          // invalid JSON
        }

        if (response.ok && responseData.data) {
          // Store the app JWT for all subsequent API calls (Zotag pattern)
          if (responseData.data.token) {
            setCachedBifrostToken(responseData.data.token);
          }
          populateUserData(responseData.data);
          this.setState({ ready: true });
        } else if (response.status === 401 || response.status === 403) {
          this.setState({
            ready: true,
            error: responseData.message || 'You are not authorized to access ZAPS.',
          });
        } else {
          this.setState({
            ready: true,
            error: responseData.message || 'Authentication failed (HTTP ' + response.status + ')',
          });
        }
      };

      // Register Bifrost.setListener() — handles OAuth redirect completion
      Bifrost.setListener((status, message) => {
        console.log(`[BifrostAuthGate] setListener callback: status=${status}, message=${message}`);
        if (status) {
          completeAuth();
        } else {
          console.warn('[BifrostAuthGate] Bifrost setListener failed:', message);
        }
      });

      // Check for existing valid Bifrost session
      let loggedIn = false;
      try {
        loggedIn = await Bifrost.isLoggedIn();
      } catch (err) {
        console.warn('[BifrostAuthGate] Bifrost.isLoggedIn() failed:', err.message);
      }

      if (!loggedIn) {
        if (isLocalDev) {
          this.setState({ ready: true });
        } else {
          console.log('[BifrostAuthGate] User not logged in, redirecting to Bifrost login...');
          redirectToLogin();
        }
        return;
      }

      // Logged in — exchange Bifrost token for app JWT
      await completeAuth();
    } catch (err) {
      console.error('BifrostAuthGate initialization failed:', err);
      this.setState({
        ready: true,
        error: `Initialization failed: ${err.message}`,
      });
    }
  }

  render() {
    const { ready, error } = this.state;

    // Loading state
    if (!ready) {
      return (
        <MuiThemeProvider muiTheme={getMuiTheme()}>
          <AppBar
            style={{ backgroundColor: '#000', textAlign: 'center' }}
            title={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <a href="/home"><img src={logo} alt='Zolo Logo' style={{ height: '50px', width: '50px', paddingTop: '7px' }} /></a>
              </div>
            }
            showMenuIconButton={false}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)' }}>
            <p style={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', color: '#666' }}>Loading...</p>
          </div>
        </MuiThemeProvider>
      );
    }

    // Error state — Access Denied
    // IMPORTANT: We render a simple AppBar (NOT <Header />), because Header
    // renders CitySelect which crashes when userdata is empty.
    if (error) {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      // Clear Bifrost/Cognito cookies so Retry/Login Again shows a fresh login window
      if (!isLocal) {
        clearBifrostSession();
      }

      return (
        <MuiThemeProvider muiTheme={getMuiTheme()}>
          <AppBar
            style={{ backgroundColor: '#000', textAlign: 'center' }}
            title={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <a href="/home"><img src={logo} alt='Zolo Logo' style={{ height: '50px', width: '50px', paddingTop: '7px' }} /></a>
              </div>
            }
            showMenuIconButton={false}
          />
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 64px)',
            textAlign: 'center',
            padding: '0 24px',
            fontFamily: 'Roboto, sans-serif',
          }}>
            <img alt='ZAPS' src={Osms} style={{ marginBottom: '24px', maxWidth: '200px' }} />
            <h1 style={{ color: '#d32f2f', fontSize: '28px', margin: '0 0 12px 0' }}>
              Access Denied
            </h1>
            {isLocal ? (
              // Local dev: show detailed error + DEV_TOKEN hint
              <React.Fragment>
                <p style={{
                  color: '#555',
                  fontSize: '14px',
                  maxWidth: '600px',
                  lineHeight: '1.6',
                  margin: '0 0 8px 0',
                  wordBreak: 'break-word',
                }}>{error}</p>
                <p style={{
                  color: '#999',
                  fontSize: '13px',
                  maxWidth: '500px',
                  margin: '0 0 24px 0',
                }}>
                  Paste a fresh staging token in <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>webapp/src/utils/authHelper.js</code> (DEV_TOKEN) and refresh.
                </p>
              </React.Fragment>
            ) : (
              // Deployed: show generic message
              <p style={{
                color: '#555',
                fontSize: '14px',
                maxWidth: '500px',
                lineHeight: '1.6',
                margin: '0 0 24px 0',
              }}>
                Your session has expired or you do not have access to ZAPS. Please login again.
              </p>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <RaisedButton
                label="Retry"
                primary={true}
                onClick={() => {
                  clearBifrostSession();
                  window.location.reload();
                }}
              />
              <RaisedButton
                label={isLocal ? 'Back to Login' : 'Login Again'}
                onClick={() => {
                  clearBifrostSession();
                  if (isLocal) {
                    window.location.href = '/';
                  } else {
                    initializeBifrost();
                    redirectToLogin();
                  }
                }}
              />
            </div>
          </div>
        </MuiThemeProvider>
      );
    }

    // Authenticated or unauthenticated (Login.js handles redirect)
    return this.props.children;
  }
}

export default BifrostAuthGate;



// WEBPACK FOOTER //
// ./src/components/BifrostAuthGate.js